import {
  BUTTON_COLOR,
  BUTTON_VARIANT,
  Button,
  Container,
  IconButton,
} from "@mozioinc/ui";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useBoolean } from "usehooks-ts";
import { ReactComponent as AirplaneSvg } from "./assets/airplane.svg";
import { ReactComponent as CalendarSvg } from "./assets/calendar.svg";
import { ReactComponent as ClockSvg } from "./assets/clock.svg";
import { ReactComponent as DottedArrow } from "./assets/dotted-arrow.svg";
import { ReactComponent as LeftAngleSvg } from "./assets/left-angle.svg";
import {
  SearchDialog,
  SearchDialogContent,
  SearchDialogTrigger,
} from "./search-dialog";
import type { TransNamespace } from "../types";

type SearchOverviewProps = {
  location: string;
  departureDateTime: Date;
  arrivalDateTime: Date;
};

const SearchOverview = ({
  location,
  departureDateTime,
  arrivalDateTime,
}: SearchOverviewProps) => {
  const { t } = useTranslation<TransNamespace>("parking:search-overview");
  const dialogOpen = useBoolean(false);

  return (
    <SearchDialog open={dialogOpen.value} onOpenChange={dialogOpen.setValue}>
      <div className="md:hidden">
        <Container className="bg-primary-500 flex items-center py-2 text-white">
          <IconButton
            variant={BUTTON_VARIANT.GHOST}
            color={BUTTON_COLOR.WHITE}
            asChild
            className="flex-none"
          >
            <Link to="/">
              <LeftAngleSvg />
            </Link>
          </IconButton>
          <div className="flex flex-1 flex-col text-center">
            <div className="text-sm font-bold">{location}</div>
            <div
              className="text-xs"
              dangerouslySetInnerHTML={{
                __html: t(
                  "{{arrivalDateTime, datetime}} -> {{departureDateTime, datetime}}",
                  {
                    arrivalDateTime,
                    departureDateTime,
                    formatParams: {
                      departureDateTime: {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                      },
                      arrivalDateTime: {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "numeric",
                        minute: "numeric",
                      },
                    },
                  },
                ),
              }}
            />
          </div>
          <SearchDialogTrigger asChild>
            <Button
              variant={BUTTON_VARIANT.GHOST}
              color={BUTTON_COLOR.WHITE}
              className="flex-none"
            >
              {t("Edit")}
            </Button>
          </SearchDialogTrigger>
        </Container>
      </div>

      <div className="bg-primary-500 hidden md:block">
        <Container className="relative flex items-center gap-6 py-12 text-sm text-white">
          <div className="absolute inset-x-0 top-7 flex items-center justify-center gap-2">
            <AirplaneSvg className="text-xl" />
            {location}
          </div>

          <Button
            variant={BUTTON_VARIANT.OUTLINE}
            color={BUTTON_COLOR.WHITE}
            asChild
            className="flex-none"
          >
            <Link from="/search" to="/" search={(prev) => prev}>
              <LeftAngleSvg className="text-xl" />
              {t("Back")}
            </Link>
          </Button>

          <div className="flex flex-1 items-center gap-3">
            <div className="flex gap-1">
              <CalendarSvg className="text-xl" />
              <div
                dangerouslySetInnerHTML={{
                  __html: t("{{arrivalDateTime, datetime}}", {
                    arrivalDateTime,
                    formatParams: {
                      arrivalDateTime: {
                        month: "long",
                        day: "numeric",
                      },
                    },
                  }),
                }}
              />
            </div>
            <div className="flex gap-1">
              <ClockSvg className="text-xl" />
              <div
                dangerouslySetInnerHTML={{
                  __html: t("{{arrivalDateTime, datetime}}", {
                    arrivalDateTime,
                    formatParams: {
                      arrivalDateTime: {
                        hour: "numeric",
                        minute: "numeric",
                      },
                    },
                  }),
                }}
              />
            </div>

            <div className="relative flex h-4 flex-1 justify-end overflow-hidden">
              <DottedArrow className="absolute h-4 w-96" />
            </div>

            <div className="flex gap-1">
              <CalendarSvg className="text-xl" />
              <div
                dangerouslySetInnerHTML={{
                  __html: t("{{departureDateTime, datetime}}", {
                    departureDateTime,
                    formatParams: {
                      departureDateTime: {
                        month: "long",
                        day: "numeric",
                      },
                    },
                  }),
                }}
              />
            </div>
            <div className="flex gap-1">
              <ClockSvg className="text-xl" />
              <div
                dangerouslySetInnerHTML={{
                  __html: t("{{departureDateTime, datetime}}", {
                    departureDateTime,
                    formatParams: {
                      departureDateTime: {
                        hour: "numeric",
                        minute: "numeric",
                      },
                    },
                  }),
                }}
              />
            </div>
          </div>

          <SearchDialogTrigger asChild>
            <Button
              variant={BUTTON_VARIANT.OUTLINE}
              color={BUTTON_COLOR.WHITE}
              className="flex-none"
            >
              {t("Edit")}
            </Button>
          </SearchDialogTrigger>
        </Container>
      </div>

      <SearchDialogContent onSubmit={dialogOpen.setFalse} />
    </SearchDialog>
  );
};

export { SearchOverview };
